"use client";

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./tooltip.module.scss";

export type TailPlacement = "above" | "below" | "left" | "right";
type TooltipAlign = "start" | "center" | "end";

interface TooltipProps {
  /** Plain-text tooltip body. Provide either `text` or `content`. */
  text?: string;
  /** Rich tooltip body (JSX). Overrides `text` when provided. */
  content?: React.ReactNode;
  dark?: boolean;
  tailPlacement?: TailPlacement;
  /**
   * Cross-axis placement against the trigger. "center" (default) centers the
   * bubble on it; "end" pins the bubble's trailing edge just past the trigger,
   * so a wide bubble hanging off a narrow icon stays inside the surrounding card
   * instead of spilling past it. "start" lines the bubble's leading edge up with
   * a wide trigger, such as a badge, and points the tail near its start.
   */
  align?: TooltipAlign;
  className?: string;
  id?: string;
  /**
   * When provided, the tooltip wraps this trigger element and reveals itself on
   * focus (or hover) of the trigger — "focus-triggered" mode. Without `children`
   * the component renders only the tooltip bubble (presentational mode).
   */
  children?: React.ReactNode;
  /** Keeps the anchor inline, for inline triggers like a badge. Click mode is inline regardless. */
  anchorInline?: boolean;
  /**
   * Positions the bubble against an element the tooltip does not wrap — for a
   * trigger that lives elsewhere in the tree and is reached by ref. The bubble
   * is portalled and anchored exactly as it is in focus-triggered mode; pass
   * `open` to control it, since there is no trigger here to open it.
   */
  anchor?: HTMLElement | null;
  /**
   * Describes the trigger with the tooltip copy. Opt-in because it clones
   * `children`: pass it only when the child is a DOM element that will honor
   * `aria-describedby`.
   *
   * With `text`, the copy is mirrored into an always-mounted visually-hidden
   * node, so it is still readable when the bubble is closed — browse-mode and
   * touch-exploration users never fire the focus event that would open it.
   * `content` cannot be mirrored (cloning it would duplicate any interactive
   * elements and ids inside), so those triggers are described only while open.
   */
  describeChild?: boolean;
  /**
   * "hover" (default) reveals on hover/focus of the trigger and auto-hides on
   * mouse-out. "click" toggles on trigger click and stays open until the user
   * dismisses it — required when the bubble holds interactive content (e.g. a
   * confirm popover with Cancel/Delete actions).
   */
  trigger?: "hover" | "click";
  /**
   * Accessible name for the bubble in "click" mode, where it is exposed as a
   * dialog rather than a tooltip. Ignored in hover mode.
   */
  label?: string;
  /** Controlled open state. When set, the component never manages `open` itself. */
  open?: boolean;
  /** Fired whenever the tooltip wants to open or close. Required for controlled use. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Smallest distance the tail's center may sit from a bubble edge: half the 16px
 * tail plus the body's 4px corner radius, so it never overhangs a rounded corner.
 */
const TAIL_EDGE_INSET = 12;

/**
 * How far an `align="end"` bubble overhangs the trailing edge of its trigger,
 * which lands the tail dead center on a 16px trigger icon.
 */
const ALIGN_END_OVERHANG = 16;

/**
 * Where an `align="start"` tail points, measured from the trigger's leading edge:
 * the tail's 16px inset from the bubble edge plus half its width.
 */
const ALIGN_START_TAIL_INSET = 24;

/** Breathing room kept between the bubble and the viewport edge. */
const VIEWPORT_MARGIN = 8;

/** Gap between the trigger and the bubble's near edge. */
const TRIGGER_GAP = 4;

const tailDirectionMap: Record<TailPlacement, string> = {
  above: "Up",
  below: "Down",
  left: "Left",
  right: "Right",
};

const oppositePlacement: Record<TailPlacement, TailPlacement> = {
  above: "below",
  below: "above",
  left: "right",
  right: "left",
};

/**
 * Room between the trigger and the viewport edge the bubble would open into.
 * Placement names the side of the *bubble* the tail sits on, so "above" opens
 * downward from the trigger and "below" opens upward.
 */
const availableSpace = (placement: TailPlacement, anchor: DOMRect): number => {
  switch (placement) {
    case "above":
      return window.innerHeight - anchor.bottom;
    case "below":
      return anchor.top;
    case "left":
      return window.innerWidth - anchor.right;
    case "right":
      return anchor.left;
  }
};

const Tooltip: React.FC<TooltipProps> = ({
  text,
  content,
  dark = false,
  tailPlacement = "above",
  align = "center",
  className,
  id,
  children,
  anchorInline = false,
  anchor: externalAnchor,
  describeChild = false,
  trigger = "hover",
  label,
  open: openProp,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const isClickTriggered = trigger === "click";
  // Click mode needs a stable target for the trigger's aria-controls even when
  // the caller hasn't supplied an id.
  const generatedId = useId();
  const dialogId = id ?? generatedId;
  const descriptionId = `${dialogId}-description`;
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const onOpenChangeRef = useRef(onOpenChange);
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChangeRef.current?.(next);
    },
    [isControlled],
  );

  const updatePosition = useCallback(() => {
    const anchor = externalAnchor ?? anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;
    const a = anchor.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    // Safe to key off the requested placement: a flip swaps sides, never axes.
    const isVerticalTail = tailPlacement === "above" || tailPlacement === "below";
    // A bubble overflowing the edge it opens toward flips to the trigger's other
    // side, but only if that side can hold it — otherwise the clamp below beats
    // a second overflow.
    const needed = (isVerticalTail ? b.height : b.width) + TRIGGER_GAP + VIEWPORT_MARGIN;
    const flipped = oppositePlacement[tailPlacement];
    const resolved =
      availableSpace(tailPlacement, a) < needed && availableSpace(flipped, a) >= needed ? flipped : tailPlacement;
    let left = 0;
    let top = 0;
    const alignedLeft =
      align === "start"
        ? a.left
        : align === "end"
          ? a.right + ALIGN_END_OVERHANG - b.width
          : a.left + a.width / 2 - b.width / 2;
    const alignedTop =
      align === "start"
        ? a.top
        : align === "end"
          ? a.bottom + ALIGN_END_OVERHANG - b.height
          : a.top + a.height / 2 - b.height / 2;
    switch (resolved) {
      case "above":
        left = alignedLeft;
        top = a.bottom + TRIGGER_GAP;
        break;
      case "below":
        left = alignedLeft;
        top = a.top - TRIGGER_GAP - b.height;
        break;
      case "left":
        left = a.right + TRIGGER_GAP;
        top = alignedTop;
        break;
      case "right":
        left = a.left - TRIGGER_GAP - b.width;
        top = alignedTop;
        break;
    }
    left = Math.min(
      Math.max(VIEWPORT_MARGIN, left),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - b.width - VIEWPORT_MARGIN),
    );
    top = Math.min(
      Math.max(VIEWPORT_MARGIN, top),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - b.height - VIEWPORT_MARGIN),
    );
    // Alignment and the viewport clamp both slide the bubble off the trigger's
    // center line, so the tail is measured from the anchor rather than from the
    // bubble, keeping it pointed at the trigger wherever the bubble lands.
    const tailTarget = isVerticalTail
      ? align === "start"
        ? a.left + ALIGN_START_TAIL_INSET
        : a.left + a.width / 2
      : align === "start"
        ? a.top + ALIGN_START_TAIL_INSET
        : a.top + a.height / 2;
    const tailCenter = tailTarget - (isVerticalTail ? left : top);
    const tailLimit = Math.max(TAIL_EDGE_INSET, (isVerticalTail ? b.width : b.height) - TAIL_EDGE_INSET);
    setCoords({
      left,
      top,
      tail: Math.min(Math.max(TAIL_EDGE_INSET, tailCenter), tailLimit),
      placement: resolved,
      bridge: bridgeBetween(resolved, a, { left, top, width: b.width, height: b.height }),
    });
  }, [tailPlacement, align, externalAnchor]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, updatePosition]);

  const show = () => setOpen(true);

  const isInBubble = (node: EventTarget | null) => node instanceof Node && !!bubbleRef.current?.contains(node);

  // The bridge spans the gap between trigger and bubble, so the pointer only
  // leaves one for the other when it is headed straight there.
  const hideUnlessEntering = (target: React.RefObject<HTMLElement | null>) => (event: React.MouseEvent) => {
    const next = event.relatedTarget;
    if (next instanceof Node && target.current?.contains(next)) {
      return;
    }
    setOpen(false);
  };

  // Moving focus into the bubble (e.g. pressing a link inside it) must not close it.
  const hideUnlessFocusingBubble = (event: React.FocusEvent) => {
    if (!isInBubble(event.relatedTarget)) {
      setOpen(false);
    }
  };

  // Touch has no hover to end, so a tap on the trigger toggles the bubble. Taps inside the
  // bubble (a React child of the anchor) and into a text field (which keeps its bubble while
  // focused) are left alone.
  const touchTapRef = useRef<{ wasOpen: boolean } | null>(null);
  const onTriggerPointerDown = (event: React.PointerEvent) => {
    const target = event.target;
    const isTextEntry =
      target instanceof Element && target.closest("input, textarea, select, [contenteditable='true']") !== null;
    touchTapRef.current =
      event.pointerType === "touch" && !isInBubble(target) && !isTextEntry ? { wasOpen: open } : null;
  };
  const onTriggerPointerCancel = () => {
    touchTapRef.current = null;
  };
  const onTriggerClick = () => {
    if (touchTapRef.current) {
      setOpen(!touchTapRef.current.wasOpen);
    }
    touchTapRef.current = null;
  };

  // The bubble stays put until a press lands outside it: click mode holds interactive content,
  // and on touch a hover bubble has no mouse-out to close it. Escape (below) applies to both modes.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const anchor = externalAnchor ?? anchorRef.current;
      if (anchor?.contains(target) || bubbleRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [open, setOpen, externalAnchor]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      setOpen(false);
      if (isClickTriggered) {
        // The click-mode bubble is the innermost interactive layer, so it
        // consumes the key and hands focus back rather than stranding the user.
        // A hover tooltip is incidental and lets Escape through, so an
        // enclosing dialog still closes on the same press.
        event.stopPropagation();
        const trigger = anchorRef.current?.querySelector<HTMLElement>("button, [href], [tabindex]") ?? externalAnchor;
        trigger?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isClickTriggered, open, setOpen, externalAnchor]);

  // Move focus into the bubble once it is positioned, so the confirm actions are
  // the next thing a keyboard user reaches.
  useEffect(() => {
    if (!isClickTriggered || !open || !coords) {
      return;
    }
    bubbleRef.current?.querySelector<HTMLElement>("button, [href], [tabindex]")?.focus();
  }, [isClickTriggered, open, coords]);

  const variant = dark ? "Dark" : "Light";
  // Tail and body follow where positioning actually landed the bubble, which a
  // flip may have moved to the trigger's other side.
  const activePlacement = coords?.placement ?? tailPlacement;
  const direction = tailDirectionMap[activePlacement];
  const isRich = content !== undefined;

  const containerClasses = [styles.Tooltip, styles[activePlacement], isRich ? styles.rich : "", className || ""]
    .filter(Boolean)
    .join(" ");

  const isVertical = activePlacement === "above" || activePlacement === "below";
  const bodyLayout = isVertical ? styles.bodyVertical : styles.bodyHorizontal;
  const bodyClasses = [styles.body, bodyLayout, styles[`body${variant}`], isRich ? styles.bodyRich : ""]
    .filter(Boolean)
    .join(" ");

  const tailClasses = [
    styles.tail,
    styles[`tail${direction}`],
    styles[`tail${direction}${variant}`],
    // Only the anchored (portalled) bubble has an anchor to point at; the
    // unanchored presentational bubble keeps its centered tail.
    coords ? styles.tailAligned : "",
  ]
    .filter(Boolean)
    .join(" ");

  const tail = <span className={tailClasses} aria-hidden="true" />;
  const body = <div className={bodyClasses}>{content ?? text}</div>;

  const isTailBefore = activePlacement === "above" || activePlacement === "left";

  const bubble = (
    <div
      className={containerClasses}
      role={isClickTriggered ? "dialog" : "tooltip"}
      aria-modal={isClickTriggered ? false : undefined}
      aria-label={isClickTriggered ? (label ?? text ?? "Confirmation") : undefined}
      id={dialogId}
    >
      {isTailBefore ? tail : body}
      {isTailBefore ? body : tail}
    </div>
  );

  const floatingBubble = (bubbleProps: Record<string, unknown>) =>
    createPortal(
      <span
        ref={bubbleRef}
        className={styles.portalFloating}
        style={
          {
            left: coords?.left ?? 0,
            top: coords?.top ?? 0,
            "--tooltip-tail-offset": `${coords?.tail ?? 0}px`,
            visibility: coords ? undefined : "hidden",
          } as React.CSSProperties
        }
        {...bubbleProps}
      >
        {!isClickTriggered && coords && (
          <span className={styles.bridge} style={coords.bridge} aria-hidden="true" data-tooltip-bridge="" />
        )}
        {bubble}
      </span>,
      document.body,
    );

  if (children === undefined) {
    // Anchored to an element elsewhere in the tree: portal and position the
    // bubble against it, with no trigger of our own to wrap.
    if (externalAnchor) {
      return open ? floatingBubble({}) : null;
    }
    return bubble;
  }

  const anchorProps = isClickTriggered
    ? {
        onClick: () => setOpen(!open),
        "aria-haspopup": "dialog" as const,
        "aria-expanded": open,
        "aria-controls": open ? dialogId : undefined,
      }
    : {
        onFocus: show,
        onBlur: hideUnlessFocusingBubble,
        onMouseEnter: show,
        onMouseLeave: hideUnlessEntering(bubbleRef),
        onPointerDown: onTriggerPointerDown,
        onPointerCancel: onTriggerPointerCancel,
        onClick: onTriggerClick,
      };

  const bubbleProps = isClickTriggered ? {} : { onMouseEnter: show, onMouseLeave: hideUnlessEntering(anchorRef) };

  const isDescribing = describeChild && React.isValidElement(children);
  // Only plain text can be mirrored into the hidden node; a `content` node would
  // bring its interactive elements and ids along with it, so those triggers fall
  // back to being described by the bubble while it is open.
  const mirrorsDescription = isDescribing && typeof text === "string";
  const describedById = mirrorsDescription ? descriptionId : isDescribing && open ? dialogId : undefined;

  const describedChild = isDescribing ? (children as React.ReactElement<{ "aria-describedby"?: string }>) : null;
  // cloneElement overwrites props rather than merging them, even with undefined, so
  // a description already on the trigger has to be carried across by hand.
  const triggerNode = describedChild
    ? React.cloneElement(describedChild, {
        "aria-describedby":
          [describedChild.props["aria-describedby"], describedById].filter(Boolean).join(" ") || undefined,
      })
    : children;

  return (
    <span
      ref={anchorRef}
      className={`${styles.anchor} ${isClickTriggered || anchorInline ? styles.anchorInline : ""}`.trim()}
      {...anchorProps}
    >
      {triggerNode}
      {mirrorsDescription && (
        <span id={descriptionId} className={styles.srOnly}>
          {text}
        </span>
      )}
      {open && floatingBubble(bubbleProps)}
    </span>
  );
};

export default Tooltip;

/**
 * The strip between the trigger and a bubble placed at `bubble`, in coordinates
 * relative to the bubble. It is as wide as the trigger and bubble together, so
 * a diagonal move from one to the other still crosses it.
 */
const bridgeBetween = (placement: TailPlacement, anchor: DOMRect, bubble: Box): Box => {
  const bubbleRight = bubble.left + bubble.width;
  const bubbleBottom = bubble.top + bubble.height;
  const spanLeft = Math.min(anchor.left, bubble.left);
  const spanTop = Math.min(anchor.top, bubble.top);
  const horizontalSpan = { left: spanLeft - bubble.left, width: Math.max(anchor.right, bubbleRight) - spanLeft };
  const verticalSpan = { top: spanTop - bubble.top, height: Math.max(anchor.bottom, bubbleBottom) - spanTop };
  switch (placement) {
    case "above":
      return { ...horizontalSpan, top: anchor.bottom - bubble.top, height: Math.max(0, bubble.top - anchor.bottom) };
    case "below":
      return { ...horizontalSpan, top: bubble.height, height: Math.max(0, anchor.top - bubbleBottom) };
    case "left":
      return { ...verticalSpan, left: anchor.right - bubble.left, width: Math.max(0, bubble.left - anchor.right) };
    case "right":
      return { ...verticalSpan, left: bubble.width, width: Math.max(0, anchor.left - bubbleRight) };
  }
};

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface TooltipCoords {
  left: number;
  top: number;
  /** Tail center, in px from the bubble's leading edge along the tail's axis. */
  tail: number;
  /** Side the bubble landed on, flipped from the requested one when it had to be. */
  placement: TailPlacement;
  /** Hit area over the gap to the trigger, relative to the bubble. */
  bridge: Box;
}
